import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { getDoctors } from '../utils/api';
import { printRegistrationSlip } from '../utils/printSlip';
import api from '../utils/api';

const NAVY = '#0d2137';
const GREEN = '#1a6b3c';
const RED = '#c41e3a';

function PatientModal({ visit, doctor, onClose }) {
  if (!visit) return null;
  const vitals = [
    { label: 'Temperature', value: visit.temperature ? `${visit.temperature} °C` : null },
    { label: 'Blood Pressure', value: (visit.bp_systolic && visit.bp_diastolic) ? `${visit.bp_systolic}/${visit.bp_diastolic} mmHg` : null },
    { label: 'Pulse', value: visit.pulse ? `${visit.pulse} bpm` : null },
    { label: 'SpO2', value: visit.spo2 ? `${visit.spo2}%` : null },
    { label: 'Weight', value: visit.weight ? `${visit.weight} kg` : null },
    { label: 'Height', value: visit.height ? `${visit.height} cm` : null },
    { label: 'BMI', value: visit.bmi ? visit.bmi : null },
  ].filter(v => v.value);

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#fff', borderRadius: 14, width: '100%', maxWidth: 560,
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)', overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{ background: NAVY, color: '#fff', padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: 17 }}>{visit.full_name}</div>
            <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{visit.mrn} · {visit.visit_token}</div>
          </div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 16 }}>✕</button>
        </div>

        <div style={{ padding: 20 }}>
          {/* Patient info */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
            {[
              ['Gender', visit.gender],
              ['Age', visit.age_years ? `${visit.age_years} years` : '—'],
              ['Phone', visit.phone],
              ['Blood Group', visit.blood_group || '—'],
              ['CNIC', visit.cnic || '—'],
              ['City', visit.city || '—'],
              ['Visit Date', visit.visit_date ? String(visit.visit_date).slice(0, 10) : '—'],
              ['Fees', `PKR ${Number(visit.fees || 0).toLocaleString()}`],
            ].map(([lbl, val]) => (
              <div key={lbl} style={{ background: '#f8fafc', borderRadius: 8, padding: '8px 12px' }}>
                <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 700, marginBottom: 2 }}>{lbl}</div>
                <div style={{ fontSize: 13, color: NAVY, fontWeight: 600 }}>{val || '—'}</div>
              </div>
            ))}
          </div>

          {/* Address */}
          {visit.address && (
            <div style={{ background: '#f8fafc', borderRadius: 8, padding: '8px 12px', marginBottom: 12 }}>
              <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 700, marginBottom: 2 }}>Address</div>
              <div style={{ fontSize: 13, color: NAVY }}>{visit.address}</div>
            </div>
          )}

          {/* Chief Complaint */}
          {visit.chief_complaint && (
            <div style={{ background: '#fef3c7', borderRadius: 8, padding: '8px 12px', marginBottom: 12 }}>
              <div style={{ fontSize: 10, color: '#92400e', fontWeight: 700, marginBottom: 2 }}>Chief Complaint</div>
              <div style={{ fontSize: 13, color: '#78350f', fontWeight: 600 }}>{visit.chief_complaint}</div>
            </div>
          )}

          {/* Doctor */}
          {doctor && (
            <div style={{ background: '#eff6ff', borderRadius: 8, padding: '8px 12px', marginBottom: 12 }}>
              <div style={{ fontSize: 10, color: '#1e40af', fontWeight: 700, marginBottom: 2 }}>Doctor</div>
              <div style={{ fontSize: 13, color: '#1e3a8a', fontWeight: 600 }}>Dr. {doctor.name} — {doctor.specialty}</div>
            </div>
          )}

          {/* Vitals */}
          {vitals.length > 0 && (
            <>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 8 }}>VITALS</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 16 }}>
                {vitals.map(({ label, value }) => (
                  <div key={label} style={{ background: '#f0fdf4', borderRadius: 8, padding: '8px 10px', textAlign: 'center' }}>
                    <div style={{ fontSize: 9, color: '#166534', fontWeight: 700 }}>{label.toUpperCase()}</div>
                    <div style={{ fontSize: 14, color: '#15803d', fontWeight: 800, marginTop: 2 }}>{value}</div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              onClick={() => printRegistrationSlip(visit, doctor, visit.mrn, visit.visit_token)}
              style={{ flex: 1, padding: '10px 0', background: NAVY, color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 13 }}>
              Print Slip
            </button>
            <button onClick={onClose} style={{ flex: 1, padding: '10px 0', background: '#f1f5f9', color: '#374151', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 13 }}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function DoctorList() {
  const today = new Date().toISOString().split('T')[0];
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const [mode, setMode] = useState('single'); // 'single' | 'range'
  const [doctors, setDoctors] = useState([]);
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [selectedDoctor, setSelectedDoctor] = useState(null);

  useEffect(() => {
    getDoctors().then(r => setDoctors(r.data)).catch(() => toast.error('Failed to load doctors'));
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = mode === 'range' ? { from, to } : { date: from };
    api.get('/visits', { params })
      .then(r => setVisits(r.data))
      .catch(() => toast.error('Failed to load visits'))
      .finally(() => setLoading(false));
  }, [from, to, mode]);

  const grouped = doctors.map(doc => ({
    doctor: doc,
    visits: visits.filter(v => String(v.doctor_id) === String(doc.id)),
  })).filter(g => g.visits.length > 0);

  const unassigned = visits.filter(v => !v.doctor_id || !doctors.find(d => String(d.id) === String(v.doctor_id)));

  const totalRevenue = visits.reduce((sum, v) => sum + Number(v.fees || 0), 0);

  const openPatient = (visit, doctor) => {
    setSelectedVisit(visit);
    setSelectedDoctor(doctor);
  };

  return (
    <div style={{ padding: '32px 28px' }}>
      {selectedVisit && (
        <PatientModal visit={selectedVisit} doctor={selectedDoctor} onClose={() => setSelectedVisit(null)} />
      )}

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ color: NAVY, fontSize: 26, fontWeight: 900, marginBottom: 4 }}>Doctor-wise Patient List</h1>
          <div style={{ color: '#64748b', fontSize: 14 }}>
            {visits.length} patient{visits.length !== 1 ? 's' : ''} · {grouped.length} doctor{grouped.length !== 1 ? 's' : ''} · PKR {totalRevenue.toLocaleString()} total
          </div>
        </div>

        {/* Date controls */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end' }}>
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              onClick={() => setMode('single')}
              style={{ padding: '6px 14px', borderRadius: 6, border: '1.5px solid #cbd5e1', background: mode === 'single' ? NAVY : '#fff', color: mode === 'single' ? '#fff' : '#374151', fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>
              Single Day
            </button>
            <button
              onClick={() => setMode('range')}
              style={{ padding: '6px 14px', borderRadius: 6, border: '1.5px solid #cbd5e1', background: mode === 'range' ? NAVY : '#fff', color: mode === 'range' ? '#fff' : '#374151', fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>
              Date Range
            </button>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 2, fontWeight: 600 }}>FROM</div>
              <input type="date" value={from} onChange={e => setFrom(e.target.value)}
                style={{ padding: '7px 12px', border: '1.5px solid #cbd5e1', borderRadius: 8, fontSize: 14, outline: 'none', color: NAVY, fontWeight: 600 }} />
            </div>
            {mode === 'range' && (
              <div>
                <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 2, fontWeight: 600 }}>TO</div>
                <input type="date" value={to} onChange={e => setTo(e.target.value)} min={from}
                  style={{ padding: '7px 12px', border: '1.5px solid #cbd5e1', borderRadius: 8, fontSize: 14, outline: 'none', color: NAVY, fontWeight: 600 }} />
              </div>
            )}
          </div>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>Loading...</div>
      ) : grouped.length === 0 && unassigned.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8', fontSize: 15 }}>No patients found.</div>
      ) : (
        <>
          {grouped.map(({ doctor, visits: dVisits }) => (
            <div key={doctor.id} style={{ background: '#fff', borderRadius: 12, marginBottom: 20, boxShadow: '0 2px 10px rgba(0,0,0,0.07)', overflow: 'hidden' }}>
              <div style={{ background: NAVY, color: '#fff', padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontWeight: 800, fontSize: 16 }}>Dr. {doctor.name}</div>
                  <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{doctor.specialty} · Room {doctor.room_number || '—'}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 22, fontWeight: 900, color: '#4ade80' }}>{dVisits.length} patients</div>
                  <div style={{ fontSize: 18, fontWeight: 900, color: '#f87171' }}>PKR {dVisits.reduce((s, v) => s + Number(v.fees || 0), 0).toLocaleString()} total</div>
                </div>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    {['#', 'Date', 'Token', 'Patient', 'Gender', 'Age', 'Phone', 'Fees', 'Actions'].map(h => (
                      <th key={h} style={{ padding: '9px 12px', fontSize: 12, fontWeight: 700, color: '#374151', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {dVisits.map((v, i) => (
                    <tr key={v.id} style={{ borderBottom: '1px solid #f1f5f9', background: i % 2 === 0 ? '#fff' : '#f8fafc' }}>
                      <td style={{ padding: '9px 12px', fontSize: 12, color: '#94a3b8' }}>{i + 1}</td>
                      <td style={{ padding: '9px 12px', fontSize: 12, color: '#64748b' }}>{v.visit_date ? String(v.visit_date).slice(0, 10) : '—'}</td>
                      <td style={{ padding: '9px 12px', fontWeight: 700, color: RED, fontSize: 13 }}>{v.visit_token}</td>
                      <td style={{ padding: '9px 12px' }}>
                        <button
                          onClick={() => openPatient(v, doctor)}
                          style={{ fontWeight: 700, color: NAVY, background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, padding: 0, textDecoration: 'underline', textDecorationColor: '#cbd5e1' }}>
                          {v.full_name || '—'}
                        </button>
                      </td>
                      <td style={{ padding: '9px 12px', fontSize: 12 }}>{v.gender || '—'}</td>
                      <td style={{ padding: '9px 12px', fontSize: 12 }}>{v.age_years ? `${v.age_years}y` : '—'}</td>
                      <td style={{ padding: '9px 12px', fontSize: 12 }}>{v.phone || '—'}</td>
                      <td style={{ padding: '9px 12px', fontSize: 12, fontWeight: 700, color: GREEN }}>PKR {Number(v.fees || 0).toLocaleString()}</td>
                      <td style={{ padding: '9px 12px', display: 'flex', gap: 6 }}>
                        <button
                          onClick={() => openPatient(v, doctor)}
                          style={{ padding: '4px 10px', background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: 5, cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>
                          Detail
                        </button>
                        <button
                          onClick={() => printRegistrationSlip(v, doctor, v.mrn, v.visit_token)}
                          style={{ padding: '4px 10px', background: NAVY, color: '#fff', border: 'none', borderRadius: 5, cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>
                          Print
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}

          {unassigned.length > 0 && (
            <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 2px 10px rgba(0,0,0,0.07)', overflow: 'hidden' }}>
              <div style={{ background: '#64748b', color: '#fff', padding: '12px 20px', fontWeight: 700 }}>
                Unassigned ({unassigned.length} patients)
              </div>
              <div style={{ padding: '12px 20px', color: '#64748b', fontSize: 13 }}>
                {unassigned.map(v => (
                  <div key={v.id} style={{ padding: '4px 0', cursor: 'pointer' }} onClick={() => openPatient(v, null)}>
                    <span style={{ fontWeight: 600, color: NAVY }}>{v.full_name || '—'}</span> · {v.visit_token} · {v.visit_date}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
